import React, { useState, useEffect } from "react";
import { Spinner, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getJwt } from "src/auth";

interface DocumentViewerProps {
  documentId: string;
  apiBaseUrl?: string;
  authToken?: string;
  expiresIn?: number;
  width?: string;
  height?: string;
  showHeader?: boolean;
  useSandbox?: boolean;
  pageNumber?: number; // Optional page number for PDFs (1-indexed)
}

interface PresignedUrlResponse {
  url: string;
  expiresIn: number;
  document: {
    id: string;
    originalname: string;
    mimetype: string;
  };
}

const isImageMimetype = (mimetype: string): boolean => {
  const imageMimetypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/tiff",
    "image/bmp",
    "image/svg+xml",
  ];
  return imageMimetypes.includes(mimetype?.toLowerCase());
};

export const PresignedDocumentViewer: React.FC<DocumentViewerProps> = ({
  documentId,
  apiBaseUrl = "/api/v1/protected",
  expiresIn = 3600,
  width = "100%",
  height = "100vh",
  showHeader = false,
  useSandbox = false,
  pageNumber,
}) => {
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documentInfo, setDocumentInfo] = useState<any>(null);
  const [imageLoadError, setImageLoadError] = useState(false);

  const authToken = getJwt();

  useEffect(() => {
    const fetchPresignedUrl = async () => {
      try {
        setLoading(true);
        setError(null);

        const headers: HeadersInit = {
          "Content-Type": "application/json",
        };
        if (authToken) {
          headers["Authorization"] = `Bearer ${authToken}`;
        }

        const response = await fetch(
          `${apiBaseUrl}/document/${documentId}/downloads3`,
          {
            method: "GET",
            headers,
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch presigned URL: ${response.statusText}`
          );
        }

        const data: PresignedUrlResponse = await response.json();
        setPresignedUrl(data.url);
        setDocumentInfo(data.document);
        setImageLoadError(false); // Reset on new document
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
        console.error("Error fetching presigned URL:", err);
      } finally {
        setLoading(false);
      }
    };

    if (documentId) {
      fetchPresignedUrl();
    }
  }, [documentId, apiBaseUrl, authToken, expiresIn]);

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center bg-light"
        style={{ width, height }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ width, height }}
      >
        <div className="text-center p-4" style={{ maxWidth: "500px" }}>
          <Alert variant="danger">
            <Alert.Heading>
              <i className="fa fa-exclamation-triangle mr-2"></i>
              Failed to load document
            </Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        </div>
      </div>
    );
  }

  if (!presignedUrl) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center bg-light text-center px-4"
        style={{ width, height }}
      >
        <FontAwesomeIcon
          icon="file-pdf"
          className="text-muted mb-3"
          style={{ fontSize: "2.5rem" }}
        />
        <p className="text-muted mb-1" style={{ fontWeight: 600 }}>
          Source PDF unavailable
        </p>
        <p className="text-muted small mb-0" style={{ maxWidth: 360 }}>
          The original document file couldn't be loaded — the data on the
          right was extracted from a previous upload. Re-upload the file to
          re-link it, or contact support if the document was deleted in
          error.
        </p>
      </div>
    );
  }

  const headerHeight = showHeader && documentInfo ? 40 : 0;
  const contentHeight =
    height === "100vh" ? `calc(${height} - ${headerHeight}px)` : height;

  const isImage = documentInfo && isImageMimetype(documentInfo.mimetype);
  return (
    <div className="position-relative" style={{ width, height }}>
      {showHeader && documentInfo && (
        <div
          className="bg-dark text-white px-3 py-2 d-flex align-items-center"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: `${headerHeight}px`,
            zIndex: 10,
            fontSize: "14px",
          }}
        >
          <span className="font-weight-bold">{documentInfo.originalname}</span>
          <span className="ml-3 text-muted">({documentInfo.mimetype})</span>
        </div>
      )}
      {isImage && !imageLoadError ? (
        <div
          style={{
            width: "100%",
            height: contentHeight,
            marginTop: headerHeight ? `${headerHeight}px` : "0",
            overflow: "auto",
            backgroundColor: "#f5f5f5",
          }}
        >
          <img
            src={presignedUrl}
            alt={documentInfo?.originalname || "Document image"}
            style={{
              maxWidth: "100%",
              height: "auto",
              display: "block",
              margin: "0 auto",
            }}
            onError={() => setImageLoadError(true)}
          />
        </div>
      ) : (
        <iframe
          src={
            pageNumber && !isImage
              ? `${presignedUrl}#page=${pageNumber}&toolbar=0`
              : presignedUrl
          }
          style={{
            width: "100%",
            height: contentHeight,
            marginTop: headerHeight ? `${headerHeight}px` : "0",
            border: "none",
          }}
          title={documentInfo?.originalname || "Document viewer"}
          {...(useSandbox && {
            sandbox: "allow-same-origin allow-scripts allow-popups allow-forms",
          })}
          allowFullScreen
        >
          Your browser does not support iframes. Please download the document to
          view it: <a href={presignedUrl}>Download Document</a>
        </iframe>
      )}
    </div>
  );
};

export default PresignedDocumentViewer;
